import type { ReactNode } from 'react';

import type {
  SavedFilterLabel,
  SavedFiltersPersistenceOptions,
} from '@/hooks/zustand/useSavedFilters';
import { removeEmptyValues } from '@/utils/queries/removeEmptyValues';

export type SavedFiltersScope =
  | { kind: 'global', }
  | { kind: 'company', companyId?: string, };

export type SavedFiltersHistoryMode = 'push' | 'replace';

export type SavedFiltersFallbackSource<TFilters> = {
  filterValues: TFilters;
  labels: SavedFilterLabel[];
};

export type SavedFiltersRenderSummaryArgs<TFilters> = {
  filterValues: TFilters;
  fallbackLabels: SavedFilterLabel[];
  className?: string;
};

/**
 * Every list that owns an isolated Saved Filters scope. Adding a collection here is all the
 * shared module needs; storage-key construction stays internal to the session.
 */
export const SAVED_FILTERS_COLLECTIONS = [
  'harvesters',
  'test-purchases',
  'manual-test-purchases',
  'enforcements',
  'sellers',
  'parent-entities',
  'listings',
] as const;

export type SavedFiltersCollection = typeof SAVED_FILTERS_COLLECTIONS[number];

/** The one complete page state a Saved Filter transition asks its page to commit. */
export type SavedFiltersPageStateCommit<TFilters> = {
  filterValues: TFilters;
  savedFilterName?: string;
  historyMode: SavedFiltersHistoryMode;
};

export type SavedFiltersPageAdapter<TFilters extends Record<string, unknown>> = {
  collection: SavedFiltersCollection;
  scope: SavedFiltersScope;
  filterValues: TFilters;
  savedFilterName?: string;
  isEnabled: boolean;
  persistence?: SavedFiltersPersistenceOptions<TFilters>;
  buildLabels: (
    filterValues: TFilters,
    fallbackLabelSources: SavedFiltersFallbackSource<TFilters>[],
  ) => SavedFilterLabel[];
  renderSummary: (args: SavedFiltersRenderSummaryArgs<TFilters>) => ReactNode;
  commitPageState: (args: SavedFiltersPageStateCommit<TFilters>) => void;
};

export const defaultHasFilters = <TFilters extends Record<string, unknown>>(
  values: TFilters,
): boolean => Object.values(removeEmptyValues({ ...values })).some((value) => (
  value !== null && value !== undefined && value !== ''
));
